import { type ReactNode, Fragment } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";

export interface BreadcrumbItem {
  label: ReactNode;
  /** When set, the crumb renders as a link/button (handled by `onNavigate` or `href`). */
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumbs — detail-page / drawer trail (DESIGN-SYSTEM §5).
 * Separator is lucide `ChevronRight`. The last item is rendered as the current
 * page (non-interactive, emphasized).
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0", className)}>
      <ol className="flex items-center gap-1 text-sm text-fg-muted">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          const interactive = !isLast && (item.href || item.onClick);
          return (
            <Fragment key={i}>
              <li className="flex min-w-0 items-center">
                {interactive ? (
                  <a
                    href={item.href ?? "#"}
                    onClick={(e) => {
                      if (item.onClick) {
                        e.preventDefault();
                        item.onClick();
                      }
                    }}
                    className="truncate rounded-sm transition-colors hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className={cn("truncate", isLast && "font-medium text-fg-primary")}
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {!isLast && (
                <li aria-hidden="true" className="flex items-center">
                  <ChevronRight size={14} strokeWidth={2} className="text-fg-muted" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
Breadcrumbs.displayName = "Breadcrumbs";
